// In-card colour switching for the theme product card.
//
// Swaps a card to a sibling colour in place (images, title, links) from the payload
// snippets/product-card.liquid emits, and the price and badges from the sibling's own
// rendered card, instead of navigating to the sibling's product page.
//
// The listener is delegated from document on purpose: cards are injected after page
// load on related products, recently viewed and shop the look, so a querySelectorAll
// at load time would only ever find homepage cards.
//
// This file is .ts rather than .js because tailwind.config.js only globs
// {css,ts,tsx} and .liquid, so classes living only in a .js file get purged from the
// production CSS. There is no tsc step, esbuild strips types without checking them,
// so these types are for the editor and for review, not for the build.

// The siblings metafield has no definition and is written by an external process, so
// every field is optional and every read is still guarded at runtime. Nothing here may
// assume a field is present because the interface says so.
interface Sibling {
    handle?: string;
    title?: string;
    price?: string;
    productLegacyResourceId?: number;
    // Hoisted off the first image by the Liquid: alt text is uniform across a sibling's
    // images on every sampled product, so carrying it per image only cost bytes.
    alt?: string;
    images?: string[];
}

// Everything a rebuilt slide needs that is not the image URL itself, lifted once off
// the slide the Liquid rendered so the replacement keeps the server's decisions.
interface SlideTemplate {
    href: string;
    sizes: string;
    imageClass: string;
    width: string;
    height: string;
    alt: string;
}

const SWATCH_SELECTOR = ".product-card__swatches a[data-sibling-handle]";
const SELECTED_SWATCH_CLASS = "rs__result-card-product__swatch-image--selected";
const MEDIA_CAROUSEL_SELECTOR = "scroll-carousel.product-card__media-carousel";

// Matches the widths list in snippets/product-card.liquid
const IMAGE_WIDTHS = [200, 300, 400, 500, 600, 700, 800, 1000, 1200, 1400, 1600, 1800];

document.addEventListener("click", onSwatchClick);

dedupeCarouselIds();
watchForInjectedCarousels();

function onSwatchClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    const swatch = target && target.closest ? (target.closest(SWATCH_SELECTOR) as HTMLAnchorElement | null) : null;
    if (!swatch) return;

    const card = swatch.closest("product-card") as HTMLElement | null;
    const sibling = card ? findSibling(card, swatch.dataset.siblingHandle) : null;

    // Nothing to swap to. Return without preventing default so the swatch navigates
    // exactly as it does today.
    if (!card || !sibling) return;

    event.preventDefault();

    try {
        swapColour(card, swatch, sibling);
    } catch (error) {
        console.error("Product card colour swap failed", error);
    }
}

// Fails closed to a plain link on anything unexpected in the payload.
function findSibling(card: HTMLElement, handle: string | undefined): Sibling | null {
    if (!handle) return null;

    const payload = card.querySelector("script.product-card__siblings");
    if (!payload) return null;

    try {
        const siblings: unknown = JSON.parse(payload.textContent || "");
        if (!Array.isArray(siblings)) return null;

        return (siblings as Sibling[]).find((sibling) => sibling && sibling.handle === handle) || null;
    } catch (error) {
        return null;
    }
}

function swapColour(card: HTMLElement, swatch: HTMLAnchorElement, sibling: Sibling): void {
    replaceCarousel(card, sibling);
    updateTitle(card, sibling.title);
    updatePrice(card, sibling);
    updateLinks(card, sibling.handle);
    updateSelectedSwatch(card, swatch);
    retargetQuickBuy(card, sibling.handle);
    retargetWishlist(card, sibling);
}

// Shopify.routes.root is "/" on this store today, but it carries the market or locale
// prefix wherever one is configured. The base theme's own ProductCard and QuickBuyModal
// both build product URLs from it, so match them rather than hardcoding "/products/".
function shopifyRoot(): string {
    const shopify = (window as unknown as { Shopify?: { routes?: { root?: string } } }).Shopify;
    return (shopify && shopify.routes && shopify.routes.root) || "/";
}

function productPath(handle: string): string {
    return shopifyRoot() + "products/" + handle;
}

// quick-buy-modal string-concatenates this attribute into its fetch URL, so the bare
// handle is both correct and a clean cache key for the theme's cachedFetch.
function retargetQuickBuy(card: HTMLElement, handle: string | undefined): void {
    if (!handle) return;

    const modal = card.querySelector("quick-buy-modal");
    if (modal) modal.setAttribute("handle", handle);
}

// Swym finds the button with
// `.product_<empi>[data-swaction="addToWishlist"][data-variant-id="<epi>"]` and builds
// the wishlist entry as `{ epi: parseInt(data-variant-id), empi: parseInt(data-product-id) }`
// (swym-ext-shopify.js, functions Aa and Ta). The variant ID and the `product_<id>` class
// are therefore part of the button's identity, not decoration: leaving either pointed at
// the colour the card started on saves a mismatched product and variant pair.
//
// The siblings metafield carries no variant ID anywhere, so it is resolved from
// /products/{handle}.js and cached per handle. The visual swap is never gated on that
// fetch. Swym rescans on swym:collections-loaded, which we fire again once it settles.
function retargetWishlist(card: HTMLElement, sibling: Sibling): void {
    const button = card.querySelector(".swym-add-to-wishlist-view-product");
    if (!button) return;

    if (sibling.productLegacyResourceId) {
        const previousId = button.getAttribute("data-product-id");
        const productId = String(sibling.productLegacyResourceId);

        if (previousId) button.classList.remove("product_" + previousId);
        button.classList.add("product_" + productId);
        button.setAttribute("data-product-id", productId);
    }

    if (sibling.handle) {
        const handle = sibling.handle;

        button.setAttribute("data-product-url", window.location.origin + productPath(handle));

        // The saved state on screen belongs to the previous colour. Swym only ever adds
        // this class (its selector is :not(.swym-added)), so clear it and let the rescan
        // decide again.
        button.classList.remove("swym-added");

        resolveFirstVariantId(handle).then((variantId) => {
            // A later swatch click may have won the race while this was in flight.
            if (!variantId || card.getAttribute("handle") !== handle) return;

            button.setAttribute("data-variant-id", String(variantId));
            document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
        });
    }

    document.dispatchEvent(new CustomEvent("swym:collections-loaded"));
}

const variantIdByHandle = new Map<string, Promise<number | null>>();

function resolveFirstVariantId(handle: string): Promise<number | null> {
    let pending = variantIdByHandle.get(handle);

    if (!pending) {
        pending = fetch(productPath(handle) + ".js")
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => {
                const variants = product ? product.variants : null;
                return Array.isArray(variants) && variants[0] ? variants[0].id : null;
            })
            .catch(() => null);

        variantIdByHandle.set(handle, pending);
    }

    return pending;
}

// ScrollCarousel wires every listener in its constructor, and only when it has more
// than one cell, so swapping the slides of a live carousel leaves it without scroll
// tracking. The element has to be replaced whole. A <template> parses the markup in an
// inert document, which is what keeps the children in place when the replacement
// upgrades on insertion. document.createElement plus appendChild does not work: the
// constructor runs before the children exist.
function replaceCarousel(card: HTMLElement, sibling: Sibling): void {
    const carousel = card.querySelector(MEDIA_CAROUSEL_SELECTOR);
    const images = Array.isArray(sibling.images) ? sibling.images.filter(isUsableImage) : [];
    if (!carousel || !sibling.handle || images.length === 0) return;

    const firstImage = carousel.querySelector("img");

    const template: SlideTemplate = {
        href: productPath(sibling.handle),
        sizes: attributeOf(firstImage, "sizes"),
        imageClass: attributeOf(firstImage, "class"),
        // Liquid's image_tag stamps width and height, and the store's card aspect ratio
        // setting is "natural", which has no CSS rule behind it in any stylesheet. The
        // intrinsic ratio is the only thing holding the slide open, so a replacement
        // without these collapses the whole card until the new image decodes.
        width: attributeOf(firstImage, "width"),
        height: attributeOf(firstImage, "height"),
        alt: typeof sibling.alt === "string" ? sibling.alt : ""
    };

    const slides = images.map((url, index) => buildSlide(url, index, template));

    const replacement = document.createElement("template");
    replacement.innerHTML =
        '<scroll-carousel id="' +
        escapeHtml(carousel.id) +
        '" class="' +
        escapeHtml(carousel.className) +
        '">' +
        slides.join("") +
        "</scroll-carousel>";

    const built = replacement.content.firstElementChild;
    if (!built) return;

    carousel.replaceWith(built);
    rebindCarouselButtons(card);
}

function isUsableImage(url: unknown): url is string {
    return typeof url === "string" && url.length > 0;
}

function attributeOf(element: Element | null, name: string): string {
    return element ? element.getAttribute(name) || "" : "";
}

// Only class strings the Liquid already emits, so nothing new for Tailwind to purge.
function buildSlide(url: string, index: number, template: SlideTemplate): string {
    const srcset = IMAGE_WIDTHS.map((width) => escapeHtml(withWidth(url, width)) + " " + width + "w").join(", ");

    return (
        '<a href="' +
        escapeHtml(template.href) +
        '" class="product-card__media snap-start' +
        (index === 0 ? " is-initial" : "") +
        '" draggable="false" data-instant>' +
        '<img class="' +
        escapeHtml(template.imageClass) +
        '" src="' +
        escapeHtml(withWidth(url, 800)) +
        '" srcset="' +
        srcset +
        '" sizes="' +
        escapeHtml(template.sizes) +
        '" alt="' +
        escapeHtml(template.alt) +
        '"' +
        optionalAttribute("width", template.width) +
        optionalAttribute("height", template.height) +
        ' draggable="false"' +
        (index === 0 ? "" : ' loading="lazy"') +
        "></a>"
    );
}

function optionalAttribute(name: string, value: string): string {
    return value ? " " + name + '="' + escapeHtml(value) + '"' : "";
}

// CDN URLs from the metafield already carry a ?v= parameter, but guard anyway.
function withWidth(url: string, width: number): string {
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
}

function escapeHtml(value: unknown): string {
    return String(value === null || value === undefined ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// Re-inserting an element runs disconnectedCallback then connectedCallback. That is how
// a prev or next button rebinds to a carousel it did not see when it first connected.
function reconnect(element: Element): void {
    const parent = element.parentElement;
    if (!parent) return;

    const nextSibling = element.nextSibling;
    parent.removeChild(element);
    parent.insertBefore(element, nextSibling);
}

function rebindCarouselButtons(card: HTMLElement): void {
    card.querySelectorAll("carousel-prev-button, carousel-next-button").forEach((button) => {
        reconnect(button);

        // The swap resets to the first slide, so prev starts disabled and next enabled.
        const control = button.firstElementChild as HTMLButtonElement | null;
        if (control) control.disabled = button.tagName === "CAROUSEL-PREV-BUTTON";
    });
}

// Nosto renders each recommendation card through the Section Rendering API, where
// section.id is always "nosto-product-card" and block.id and quick_buy_context are both
// empty. Two placements holding the same product therefore emit the same carousel id,
// and <carousel-prev-button> resolves its target with document.getElementById, so the
// second card's arrows would drive the first card's carousel.
//
// Liquid cannot break the tie: a Section Rendering call has no page context, and
// `'now' | date` is documented as reflecting the time the Liquid was last rendered and
// may be cached, so it is not reliably unique either. Only the page knows, so the page
// fixes it.
let carouselSequence = 0;

function dedupeCarouselIds(): void {
    document.querySelectorAll(MEDIA_CAROUSEL_SELECTOR + "[id]").forEach((carousel) => {
        // getElementById returns the first match, so any other element with that id is
        // the one whose buttons are pointed at the wrong carousel.
        if (document.getElementById(carousel.id) === carousel) return;

        const card = carousel.closest("product-card");
        if (!card) return;

        const previousId = carousel.id;
        carousel.id = previousId + "-" + ++carouselSequence;

        card.querySelectorAll('[aria-controls="' + previousId + '"]').forEach((control) => {
            control.setAttribute("aria-controls", carousel.id);
            reconnect(control);
        });
    });
}

function watchForInjectedCarousels(): void {
    if (!document.body) return;

    const observer = new MutationObserver((records) => {
        const addedCarousel = records.some((record) =>
            Array.from(record.addedNodes).some((node) => {
                if (node.nodeType !== Node.ELEMENT_NODE) return false;

                const element = node as Element;
                return element.matches(MEDIA_CAROUSEL_SELECTOR) || !!element.querySelector(MEDIA_CAROUSEL_SELECTOR);
            })
        );

        if (addedCarousel) dedupeCarouselIds();
    });

    observer.observe(document.body, { childList: true, subtree: true });
}

function updateTitle(card: HTMLElement, title: string | undefined): void {
    if (!title) return;

    const link = card.querySelector(".product-title");
    if (link) link.textContent = title;
}

const PRICE_LIST_SELECTOR = ".product-card__info price-list";
const BADGE_LIST_SELECTOR = ".product-card__figure badge-list";

// The metafield's minPrice is not the selling price: on marked-down colours it holds the
// full price, and on some siblings it is stale. So the price and badges come from the
// sibling's own card, rendered by Shopify through the same section Nosto uses, which
// carries the live sale price, compare at price and translated badge text. The old
// metafield path is kept only as the fallback when that request fails.
function updatePrice(card: HTMLElement, sibling: Sibling): void {
    const handle = sibling.handle;
    if (!handle) return;

    fetchSiblingCard(handle).then((siblingCard) => {
        // A later swatch click may have won the race while this was in flight.
        if (card.getAttribute("handle") !== handle) return;

        if (!siblingCard || !replaceFromCard(card, siblingCard)) showMetafieldPrice(card, sibling.price);
    });
}

const siblingCardByHandle = new Map<string, Promise<Element | null>>();

function fetchSiblingCard(handle: string): Promise<Element | null> {
    let pending = siblingCardByHandle.get(handle);

    if (!pending) {
        pending = fetch(productPath(handle) + "?section_id=nosto-product-card")
            .then((response) => (response.ok ? response.text() : null))
            .then((html) => (html ? new DOMParser().parseFromString(html, "text/html").querySelector("product-card") : null))
            .catch(() => null);

        // Drop failures so the next click retries instead of replaying the fallback.
        pending.then((siblingCard) => {
            if (!siblingCard) siblingCardByHandle.delete(handle);
        });

        siblingCardByHandle.set(handle, pending);
    }

    return pending;
}

// Scoped to the info block: a quick buy modal that has been opened holds its own
// price-list earlier in the card.
function replaceFromCard(card: HTMLElement, siblingCard: Element): boolean {
    const priceList = card.querySelector(PRICE_LIST_SELECTOR);
    const nextPriceList = siblingCard.querySelector(PRICE_LIST_SELECTOR);
    if (!priceList || !nextPriceList) return false;

    priceList.replaceWith(document.importNode(nextPriceList, true));

    // A colour can differ in sale state, sold out state and custom badges, so the whole
    // list follows the sibling, including appearing or disappearing.
    const badgeList = card.querySelector(BADGE_LIST_SELECTOR);
    const nextBadgeList = siblingCard.querySelector(BADGE_LIST_SELECTOR);
    const nextBadges = nextBadgeList ? document.importNode(nextBadgeList, true) : null;

    if (badgeList && nextBadges) badgeList.replaceWith(nextBadges);
    else if (badgeList) badgeList.remove();
    else if (nextBadges) {
        const figure = card.querySelector(".product-card__figure");
        if (figure) figure.prepend(nextBadges);
    }

    return true;
}

function showMetafieldPrice(card: HTMLElement, price: string | undefined): void {
    if (!price) return;

    const priceList = card.querySelector(PRICE_LIST_SELECTOR);
    const salePrice = priceList ? priceList.querySelector("sale-price") : null;
    if (!priceList || !salePrice) return;
    if (priceText(salePrice) === price) return;

    setPriceText(salePrice, price);

    // The metafield carries no compare at price, so the rendered saving belongs to the
    // colour the card started on. Clear the sale presentation rather than show a wrong
    // saving against a new price.
    salePrice.classList.remove("text-on-sale");

    const compareAtPrice = priceList.querySelector("compare-at-price");
    if (compareAtPrice) compareAtPrice.remove();

    const onSaleBadge = card.querySelector("on-sale-badge");
    if (onSaleBadge) onSaleBadge.remove();
}

function priceText(salePrice: Element): string {
    return Array.from(salePrice.childNodes)
        .filter((node) => !isScreenReaderOnly(node))
        .map((node) => node.textContent || "")
        .join("")
        .trim();
}

function setPriceText(salePrice: Element, price: string): void {
    Array.from(salePrice.childNodes).forEach((node) => {
        if (!isScreenReaderOnly(node)) node.remove();
    });

    salePrice.append(document.createTextNode(price));
}

function isScreenReaderOnly(node: ChildNode): boolean {
    return node.nodeType === Node.ELEMENT_NODE && (node as Element).classList.contains("sr-only");
}

function updateLinks(card: HTMLElement, handle: string | undefined): void {
    if (!handle) return;

    const href = productPath(handle);

    // Swatch links are absolute, built from shop.url, so this selector leaves them
    // alone. That is what keeps the colour row working with JavaScript disabled.
    card.querySelectorAll('a[href^="' + shopifyRoot() + 'products/"]').forEach((link) => {
        link.setAttribute("href", href);
    });

    // Shop the look reads this attribute to build its "View product" link.
    card.setAttribute("handle", handle);
}

function updateSelectedSwatch(card: HTMLElement, swatch: HTMLAnchorElement): void {
    card.querySelectorAll("." + SELECTED_SWATCH_CLASS).forEach((image) => {
        image.classList.remove(SELECTED_SWATCH_CLASS);
    });

    const image = swatch.querySelector("img");
    if (image) image.classList.add(SELECTED_SWATCH_CLASS);
}
